import { FC, useEffect, useState } from "react";
import close from "assets/images/close.svg";
import Button from "components/particles/primary-button";
import { Container, StyledReactModal } from "./style";
import placeHolder from "assets/images/placeholder.jpg";
import NoDataFound from "components/particles/no-data-found";

import { dispatch } from "store";
import { setModalClosed } from "store/user-slice";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { DietaryLinkedIngredientDTO } from "utils/helpers/models/category-linked-ingredient.dto";
import useCategories from "containers/categories/useHook";

interface CategoriesModelProps {
  id: number;
  name?: string;
  data: any[];
  onRefresh?: () => void;
}
const DietariesSelectIngredientModel: FC<CategoriesModelProps> = ({
  id,
  name,
  data,
  onRefresh,
}) => {
  const { dietariesSelectIngredientModalIsOpen, isLoading } = useSelector(
    (state: any) => state.user
  );
  const [selectedIngredients, setSelectedIngredients] = useState<number[]>([]);
  const [searchDietaryIngredients, setSearchDietaryIngredients] = useState<
    any[]
  >([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const { searchAvailableIngredientsForDietary, postDietaryLinkedIngredient } =
    useCategories();
  const { setValue, handleSubmit } = useForm<DietaryLinkedIngredientDTO>({
    defaultValues: {
      ingredientIds: [],
    },
  });

  function closeModal() {
    dispatch(setModalClosed("Dietaries Select Ingredient"));
  }

  const [searchQuery, setSearchQuery] = useState({
    name: "",
  });

  const search = async () => {
    if (searchQuery.name.trim() !== "") {
      setIsSearchActive(true);
      setSearchDietaryIngredients([]);
      await searchAvailableIngredientsForDietary(
        id,
        searchQuery.name,
        setSearchDietaryIngredients
      );
    } else {
      setIsSearchActive(false);
      setSearchDietaryIngredients([]);
    }
  };

  const onSubmit = async (formData: DietaryLinkedIngredientDTO) => {
    try {
      await postDietaryLinkedIngredient(id, formData);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error linking ingredients:", error);
    }
  };

  const resetFields = () => {
    setSearchQuery({ name: "" });
    setSelectedIngredients([]);
    setSearchDietaryIngredients([]);
    setIsSearchActive(false);
  };

  useEffect(() => {
    setValue("ingredientIds", selectedIngredients);
  }, [selectedIngredients, setValue]);

  const handleCheckboxChange = (ingredientId: number) => {
    setSelectedIngredients((prevSelected) =>
      prevSelected.includes(ingredientId)
        ? prevSelected.filter((itemId) => itemId !== ingredientId)
        : [...prevSelected, ingredientId]
    );
  };

  const displayedData = isSearchActive ? searchDietaryIngredients : data;

  const displayedIds = displayedData
    .map((item: { id: number }) => item.id)
    .filter((id: number) => id != null);

  const allDisplayedSelected =
    displayedIds.length > 0 &&
    displayedIds.every((id) => selectedIngredients.includes(id));

  const handleToggleSelectAll = () => {
    if (allDisplayedSelected) {
      setSelectedIngredients((prev) =>
        prev.filter((id) => !displayedIds.includes(id))
      );
    } else {
      setSelectedIngredients(displayedIds);
    }
  };

  return (
    <StyledReactModal
      isOpen={dietariesSelectIngredientModalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Select Dietary Ingredients Modal"
    >
      <Container>
        <div className="close-button" onClick={closeModal}>
          <img src={close} style={{ cursor: "pointer" }} alt="img" />
        </div>
        <div className="popup-title">
          <h1>
            {name
              ? `Link Ingredients to ${name}`
              : "Select Dietary Ingredients"}
          </h1>
        </div>
      </Container>
      <div className="search-container">
        <div className="input-fields">
          <input
            type="text"
            placeholder="Search By Name"
            value={searchQuery.name}
            onChange={(e) => {
              setSearchQuery({ ...searchQuery, name: e.target.value });
            }}
          />
        </div>
        <Button title="Search" width="16rem" fill="true" onclick={search} />
        <Button
          title="Reset"
          width="16rem"
          fill="true"
          backgroundcolor="var(--reset-button)"
          onclick={resetFields}
        />
        <Button
          title={
            allDisplayedSelected
              ? "Unselect All Ingredients"
              : "Select All Ingredients"
          }
          width="22rem"
          fill="true"
          backgroundcolor="var(--reset-button)"
          onclick={handleToggleSelectAll}
          disabled={displayedIds.length === 0}
        />
      </div>
      <Container onSubmit={handleSubmit(onSubmit)}>
        <div className="categories-model-ingredient-container p-custom-scrollbar-8 custom-scrollbar">
          <div className="categories-container">
            {isLoading ? (
              <div className="modal-loader">Loading...</div>
            ) : displayedData.length > 0 ? (
              displayedData.map((item: any, index: number) => (
                <div className="ingredient-data" key={item?.id ?? index}>
                  <input
                    type="checkbox"
                    checked={selectedIngredients.includes(item.id)}
                    onChange={() => handleCheckboxChange(item.id)}
                  />
                  <img
                    src={
                      item?.image &&
                      !item?.image.includes(
                        `${process.env.REACT_APP_BASE_URL}images/placeholder`
                      )
                        ? item?.image
                        : placeHolder
                    }
                    alt="img"
                  />
                  <h5>{item.name}</h5>
                </div>
              ))
            ) : (
              <NoDataFound isShow={true} />
            )}
          </div>
        </div>
        <div className="add-button">
          {selectedIngredients.length === 0 ? (
            <div style={{ pointerEvents: "none", opacity: 0.5 }}>
              <Button
                title="Add"
                width="51.8rem"
                backgroundcolor="var(--primary)"
                fill="true"
              />
            </div>
          ) : (
            <Button
              title="Add"
              width="51.8rem"
              backgroundcolor="var(--primary)"
              fill="true"
            />
          )}
        </div>
      </Container>
    </StyledReactModal>
  );
};
export default DietariesSelectIngredientModel;
